import './App.css';
import { BrowserRouter as Router, Route, Routes, Navigate, Outlet, useLocation } from "react-router-dom";
import Businesses from "./pages/Businesses.js";
import Reviews from "./pages/Reviews.js";
import Login from './pages/Login.js';
import Dashboard from './pages/Dashboard.js';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BusinessProvider } from './context/BusinessContext';
import { ReviewsProvider } from './context/ReviewsContext';
import SortOptionProvider from './context/SortOptionProvider';
import LayoutWithHeader from './components/LayoutWithHeader';
import Landing from './pages/Landing.js';
import { useEffect, useState } from 'react';
import SuccessPage from './pages/SuccessPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import About from './pages/About';
import CancelPage from './pages/CancelPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminPage from './pages/AdminPage';
import CustomCategoriesSettings from './components/settings/CustomCategoriesSettings';
import SubjectMatrixPage from './pages/SubjectMatrixPage';
import SalesData from './pages/SalesData';
import Demo from './pages/Demo';
import theme from './themes/theme';
import DemoTooltipManager from './components/demo/DemoTooltipManager';

// App component - just theme provider and router
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppRoutes />
      </Router>
    </ThemeProvider>
  );
}

// Component containing all the routes and authentication logic
const AppRoutes = () => {
  return (
    <Routes>
      {/* Protected routes handled by AuthWrapper */}
      <Route path="/" element={<AuthWrapper />}>
        <Route index element={<Dashboard />} />
        <Route path="businesses" element={<Businesses />} />
        <Route path="reviews" element={<Reviews />} />
        <Route path="sales-data" element={<SalesData />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="settings/custom-categories" element={<CustomCategoriesSettings />} />
        <Route path="timeline" element={<SubjectMatrixPage />} />
      </Route>
      
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/signup" element={<Navigate to="/#signup-section" replace />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/about" element={<About />} />
      <Route path="/cancel" element={<CancelPage />} />
      <Route path="/success" element={<SuccessPage />} />
      {/* Password Reset Routes */}
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
      <Route path="/demo" element={<Demo />} />
    </Routes>
  );
};

const AuthWrapper = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasBusiness, setHasBusiness] = useState(true);
  const [isSubActive, setIsSubActive] = useState(null);
  const location = useLocation();
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const checkAuthAndStatus = async () => {
      setLoading(true);
      setIsAuthenticated(false);
      setIsSubActive(false);
      setHasBusiness(false);

      try {
        const userResponse = await fetch(`${process.env.REACT_APP_API_URL}/users/`, { credentials: 'include' });
        if (userResponse.status === 401) {
          // Not logged in, treat as unauthenticated (not an error)
          setIsAuthenticated(false);
          setIsSubActive(false);
          setHasBusiness(false);
          setUserRole(null);
          setLoading(false);
          return;
        }
        const authenticated = userResponse.ok;
        setIsAuthenticated(authenticated);
        
        if (authenticated) {
          setIsSubActive(false);
          setHasBusiness(false);

          try {
            const subStatusResponse = await fetch(`${process.env.REACT_APP_API_URL}/users/subscription-status`, { credentials: 'include' });
            if (subStatusResponse.ok) {
              const subStatusData = await subStatusResponse.json();
              setIsSubActive(subStatusData.is_active);
            } else {
              setIsSubActive(false);
            }
          } catch (subError) {
            setIsSubActive(false);
          }

          const businessesResponse = await fetch(`${process.env.REACT_APP_API_URL}/businesses/`, {
            credentials: 'include',
          });
          if (businessesResponse.ok) {
             const businessesData = await businessesResponse.json();
             // Check for monitored businesses only
             const monitoredBusinesses = businessesData ? businessesData.filter(business => business.is_monitored === true) : [];
             const userHasMonitoredBusinesses = monitoredBusinesses.length > 0;
             setHasBusiness(userHasMonitoredBusinesses);
          } else {
             setHasBusiness(false);
          }

          const userData = await userResponse.json();
          setUserRole(userData.role);

        } else {
          setIsAuthenticated(false);
          setIsSubActive(false);
          setHasBusiness(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
        setIsSubActive(false);
        setHasBusiness(false);
      } finally {
        setLoading(false);
      }
    };
    checkAuthAndStatus();
  }, []);

  if (loading) return <div>Loading...</div>;
  
  if (isAuthenticated && !isSubActive && !['/businesses', '/success', '/cancel'].includes(location.pathname)) {
      return <Navigate to="/businesses" replace />;
  }
  
  if (isAuthenticated && isSubActive && !hasBusiness && !['/businesses'].includes(location.pathname)) {
    return <Navigate to="/businesses" replace />;
  }
  
  if (isAuthenticated && userRole !== 'admin' && location.pathname === '/admin') {
    return <Navigate to="/" replace />;
  }
  
  if (isAuthenticated && userRole !== 'admin' && location.pathname === '/sales-data') {
    return <Navigate to="/" replace />;
  }
  
  if (!isAuthenticated && !['/login', '/signup', '/privacy', '/terms', '/about', '/cancel', '/', '/success'].includes(location.pathname)) {
    return <Navigate to="/" replace />;
  }

  return (
    <BusinessProvider>
      <ReviewsProvider>
        <SortOptionProvider>
          {isAuthenticated ? (
            <DemoTooltipManager>
              <LayoutWithHeader userRole={userRole}>
                 <Outlet /> 
              </LayoutWithHeader>
            </DemoTooltipManager>
          ) : (
            location.pathname === '/' ? <Landing /> : <Outlet /> 
          )}
        </SortOptionProvider>
      </ReviewsProvider>
    </BusinessProvider>
  );
};

const PublicRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/`, { credentials: 'include' });
        if (response.status === 401) {
          setIsAuthenticated(false); // Not logged in, not an error
        } else {
          setIsAuthenticated(response.ok);
        }
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  if (loading) return <div>Loading...</div>;
  return isAuthenticated ? <Navigate to="/" replace /> : children;
};

export default App;
